import React, { useMemo } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddCircle from '@mui/icons-material/AddCircle'

const getId = (value) => (typeof value === 'string' ? value : value?._id)

// Match on name, town, city and postcode so "Norwich" or "NR4" finds the
// hospital even when the user doesn't know its exact name
const filterOrganisationOptions = (options, { inputValue }) => {
  const query = (inputValue || '').trim().toLowerCase()
  if (!query) return options
  return options.filter((org) =>
    [
      org?.orgName,
      org?.orgAddressTown,
      org?.orgAddressCity,
      org?.orgAddressPostCode,
    ]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(query)),
  )
}

const OrganisationSelector = ({
  value,
  options = [],
  onChange,
  disabled = false,
  label = 'Organisation',
  hideHelperText,
  showAddButton = true,
  onAddOrganisation,
  fullWidth,
  ...props
}) => {
  const selectedOrganisation = useMemo(() => {
    const id = getId(value)
    if (!id) return null

    return options.find((option) => option._id === id) || null
  }, [value, options])

  const addressRenderer = (org) => {
    const addressAr = [
      org?.orgAddressLineOne,
      org?.orgAddressLineTwo,
      org?.orgAddressTown,
      org?.orgAddressCity,
      org?.orgAddressPostCode,
    ]
    return addressAr.filter(Boolean).join(', ')
  }

  return (
    <Autocomplete
      options={options}
      value={selectedOrganisation}
      fullWidth={fullWidth}
      disabled={disabled}
      getOptionLabel={(option) => option?.orgName || ''}
      filterOptions={filterOrganisationOptions}
      isOptionEqualToValue={(option, value) => option?._id === value?._id}
      onChange={(event, newValue) => {
        onChange(newValue || null)
      }}
      autoComplete="off"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          autoComplete="off"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}

                {showAddButton && !selectedOrganisation && (
                  <InputAdornment position="end">
                    <Tooltip title="Add a new organisation">
                      <IconButton onClick={onAddOrganisation}>
                        <AddCircle color="anGreen" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )}
                {/* TODO: Allow editing of Organisation from the Booking Screen. */}
                {/* {selectedOrganisation && (
                    <InputAdornment position="end">
                      <Tooltip
                        title={`Edit ${selectedOrganisation?.orgName ?? ''}`}
                      >
                        <IconButton onClick={() => {}}>
                          <Edit color="contrast" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )} */}
              </>
            ),
          }}
          helperText={!hideHelperText && addressRenderer(selectedOrganisation)}
        />
      )}
      {...props}
    />
  )
}

export default OrganisationSelector
